<template lang="html">
  <div>
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 h-full">
      <div class="flex flex-col h-full">
        <div class="flex-grow">
          <div>
            <p>Title <span class="text-red-500">*</span></p>
            <input
              v-model="title"
              class="bg-input text-sm text-gray-600 flex-grow outline-none px-3 py-2 w-full rounded placeholder-gray-400"
              :placeholder="'Course Tittle'"
              @keyup="checkIfSettingsHaveChanged"
              type="text"
              :disabled="!is_course_author"
            />
          </div>
          <div class="mt-5">
            <p class="mt-2">Section</p>
            <input
              v-model="section"
              class="bg-input text-sm text-gray-600 flex-grow outline-none px-3 py-2 w-full rounded placeholder-gray-400"
              :placeholder="'Course Section'"
              @keyup="checkIfSettingsHaveChanged"
              type="text"
              :disabled="!is_course_author"
            />
          </div>
          <div
            v-if="hasSchool && is_course_author"
            class="flex items-center mt-5"
          >
            <pl-checkbox
              class="pl-7"
              v-model="is_school_course"
              @change="checkIfSettingsHaveChanged"
            />
            <p>
              This is a course in my school:
              <span class="text-sm text-gray-400 mx-1">
                {{ school.name }}
              </span>
            </p>
          </div>
        </div>
        <div v-if="is_course_author" class="mt-7 flex justify-between">
          <div>
            <button
              @click="saveSettings"
              :disabled="!settings_have_changed || title.trim() === ''"
              class="px-8 py-2 bg-primary rounded cursor-pointer text-white disabled:opacity-50"
            >
              Save Settings
            </button>
          </div>
          <div class="md:flex md:ml-2.5">
            <button
              v-if="!course_is_archived"
              @click="show_archive_course_modal = true"
              class="mr-2.5 px-8 py-2 bg-blueGray-500 hover:bg-blueGray-600 rounded cursor-pointer text-white"
            >
              Archive Course
            </button>
            <button
              v-else
              @click="archiveCourse"
              class="px-8 py-2 mr-2.5 bg-gray-500 hover:bg-gray-600 rounded cursor-pointer text-white"
            >
              Restore Course
            </button>
            <button
              @click="show_delete_course_modal = true"
              class="px-8 py-2 bg-red-500 hover:bg-red-600 rounded cursor-pointer text-white"
            >
              Delete Course
            </button>
          </div>
        </div>
      </div>
      <!-- Co-teaching Settings -->
      <div class="co-teachers lg:mt-0 md:mt-6 sm:mt-6">
        <template v-if="can_add_co_teachers">
          <div class="w-full" v-if="getProfile.role === 'admin'">
            <!-- <div class="flex flex-col title"> -->
            <h6 class="font-semibold text-sm">Author</h6>
            <!-- </div> -->
            <div class="flex flex-row items-center col-xs-8 p-2 my-2">
              <img
                :src="class_author.avatar_url"
                class="rounded-full h-11 w-11"
              />
              <div class="ml-4">
                <p class="text-sm">{{ class_author.name }}</p>
                <p class="text-xs text-gray-400">{{ class_author.email }}</p>
              </div>
            </div>
          </div>
          <div class="flex flex-col mb-3">
            <h6 class="font-semibold text-sm">Co-Teachers</h6>
            <p class="text-gray-400 text-xs">
              Invite other teachers to the course (Optional)
            </p>
          </div>
          <div
            class="mb-4 px-3 py-2 flex items-center bg-gray-200 rounded text-gray-600"
          >
            <i class="fa-solid fa-circle-info text-sm mr-2"></i>
            <p
              class="w-full subpixel-antialiased select-none text-xs flex-grow outline-none mr-2"
            >
              You can only invite teachers that have already signed up to Parlay
              *
            </p>
          </div>
          <div
            v-for="coTeacher in co_teachers_in_class"
            :key="coTeacher.id"
            class="flex row flex-row justify-between items-center p-2 w-full"
          >
            <div class="flex flex-row items-center col-xs-8 p-0">
              <img :src="coTeacher.avatar_url" class="rounded-full w-11 h-11" />
              <div class="ml-4">
                <p class="text-sm">{{ coTeacher.name }}</p>
                <p class="text-xs text-gray-400">{{ coTeacher.email }}</p>
              </div>
            </div>

            <div
              v-if="!coTeacher.subscription.created_from_clever"
              class="flex"
            >
              <pl-button
                v-if="!can_add_co_teachers"
                class="pl-ruby-font float-right"
                :disabled="true"
              >
                Co-Teacher
              </pl-button>
              <button
                v-else
                class="text-red-500 mr-1 hover:text-red-600"
                @click="selectCoTeacherToRemove(coTeacher)"
              >
                <i class="text-base fa-solid fa-trash-can"></i>
              </button>
            </div>
          </div>
          <div
            v-if="can_add_co_teachers"
            class="flex justify-between w-full mt-5"
          >
            <input
              v-model.trim="co_teacher_email_to_invite"
              class="bg-input text-sm mr-3 text-gray-600 flex-grow outline-none px-3 py-2 w-full rounded placeholder-gray-400"
              :placeholder="
                co_teachers_in_class.length >= allowed_number_of_co_teachers
                  ? ''
                  : `Enter a Parlay Teacher's Email`
              "
              @keyup="email_error = ''"
              @keyup.enter="inviteCoTeacher"
              type="text"
              :errorMessage="email_error"
              :disabled="
                co_teachers_in_class.length >= allowed_number_of_co_teachers
              "
            />
            <button
              @click="inviteCoTeacher"
              :disabled="
                co_teachers_in_class.length >= allowed_number_of_co_teachers ||
                co_teacher_email_to_invite.trim() === ''
              "
              class="px-8 py-2 bg-primary rounded cursor-pointer text-white font-semibold disabled:opacity-50"
            >
              Invite
            </button>
          </div>
          <div
            v-if="admins_in_course.length"
            class="flex flex-col text-sm mt-7"
          >
            <span class="font-semibold"> Admins </span>
          </div>
          <div
            v-for="admin in admins_in_course"
            :key="admin.id"
            class="flex row flex-row items-center p-2 w-full"
          >
            <div class="flex flex-row">
              <img :src="admin.avatar_url" class="rounded-full w-11 h-11" />
              <div class="ml-2">
                <p class="text-sm">{{ admin.name }}</p>
                <br />
                <p class="text-xs text-gray-400">{{ admin.email }}</p>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="w-full">
            <div class="flex flex-col title">
              <h6 class="font-bold">Author</h6>
            </div>
            <div
              class="flex row flex-row items-center"
              style="padding: 10px; width: 100%"
            >
              <img
                :src="class_author.avatar_url"
                class="rounded-full h-11 w-11"
              />
              <div class="ml-2">
                <span class="pl-subheading">{{ class_author.name }}</span>
                <br />
                <span class="pl-caption">{{ class_author.email }}</span>
              </div>
            </div>
            <div class="flex flex-col title">
              <span class="font-bold"> Co-teachers </span>
            </div>
            <div>
              <div v-if="getCourseDetail.is_sample === true">
                <div class="flex flex-row mt-3">
                  <img
                    :src="getProfile.avatar_url"
                    class="rounded-full w-11 h-11"
                  />
                  <div class="ml-4">
                    <span class="pl-subheading">{{ getProfile.name }}</span>
                    <br />
                    <span class="pl-caption">{{ getProfile.email }}</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <div
                  v-for="co_teacher in co_teachers_in_class"
                  :key="co_teacher.id"
                  class="flex row flex-row items-center p-3 w-full"
                >
                  <div class="flex flex-row">
                    <img
                      :src="co_teacher.avatar_url"
                      class="rounded-full w-11 h-11"
                    />
                    <div class="ml-4">
                      <span class="pl-subheading">{{ co_teacher.name }}</span>
                      <br />
                      <span class="pl-caption">{{ co_teacher.email }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="admins_in_course.length"
              class="flex flex-col title mt-2"
            >
              <span class="font-bold">Admins</span>
            </div>
            <div
              v-for="admin in admins_in_course"
              :key="admin.id"
              class="flex row flex-row items-center p-3 w-full"
            >
              <div class="flex flex-row">
                <img :src="admin.avatar_url" class="rounded-full w-11 h-11" />
                <div class="ml-2">
                  <span class="pl-subheading">{{ admin.name }}</span>
                  <br />
                  <span class="pl-caption">{{ admin.email }}</span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <!-- Modals -->
    <pl-confirm
      v-if="show_confirm_co_teacher_removal"
      :message="'Remove Co-Teacher'"
      :confirmColor="'bg-red-500 hover:bg-red-600'"
      :confirmText="'DELETE'"
      :typeConfirm="false"
      @close="show_confirm_co_teacher_removal = false"
      @confirm="removeCoTeacher"
    >
      <div class="text-center my-5 text-gray-700 mb-6">
        <i
          class="fa-solid fa-circle-exclamation text-red-500 text-3xl mb-3"
        ></i>
        <p class="">
          Are you sure you want to remove
          <span class="font-bold">{{
            co_teacher_selected_to_remove.name
          }}</span>
          from the course?
        </p>
      </div>
    </pl-confirm>

    <!-- Course Deletion -->
    <DialogDeleteConfirm
      v-if="show_delete_course_modal"
      @close="show_delete_course_modal = false"
      @actionConfirmed="deleteCourse"
      subHeader="This course and its contents cannot be restored after you take this action."
      optionalText="Delete Course"
    />
    <!-- Course Archive -->
    <DialogDeleteConfirm
      v-if="show_archive_course_modal"
      @close="show_archive_course_modal = false"
      header="Are you sure want to archive this"
      submitButton="Archive"
      labelText='Please type in "confirm" to archive this item'
      subHeader="Archiving this course will stop students from being able to see or enter the course. You can restore the course at any time."
      @actionConfirmed="archiveCourse"
      optionalText="Archive Course"
    />
  </div>
</template>
<script>
  import Vue from 'vue';
  import store from 'store';
  import { mapGetters } from 'vuex';
  import { EventBus } from 'utils/event-bus';
  import updateCourse from 'services/courses/updateCourse';
  import inviteCoTeacher from 'services/courses/inviteCoTeacher';
  import adminServices from 'services/admin/adminServices';
  import removeCoTeacher from 'services/courses/removeCoTeacher';
  import find from 'lodash/find';
  import courseSocketMixin from 'mixins/sockets/courseSocketEvents';

  export default Vue.extend({
    components: {
      DialogDeleteConfirm: () => import('components/dialogDeleteConfirm'),
    },
    data() {
      return {
        allowed_number_of_co_teachers: 1000,
        title: '',
        section: '',
        settings_have_changed: false,
        show_delete_course_modal: false,
        show_archive_course_modal: false,
        co_teacher_email_to_invite: '',
        co_teacher_selected_to_remove: {},
        show_confirm_co_teacher_removal: false,
        email_error: '',
        course_is_archived: null,
        is_school_course: false,
        allow_coteachers: true,
      };
    },
    mixins: [courseSocketMixin],
    computed: {
      ...mapGetters([
        'getCourseDetail',
        'getRoleInCourse',
        'getProfile',
        'getCourseLayoutFullScreenStatus',
      ]),
      courseSettingsTitle() {
        const section = this.section ? `- ${this.section}` : '';
        return `${this.title} ${section} - Course Settings`;
      },
      invite_code() {
        const invites = this.getCourseDetail.invites[0];
        return invites ? invites.code : 'loading...';
      },
      admins_in_course() {
        return this.getCourseDetail.users.filter((user) => {
          return user.subscription.role === 'admin';
        });
      },
      class_author() {
        return find(this.getCourseDetail.users, (el) => {
          return el.subscription.role === 'author';
        });
      },
      school() {
        return this.getProfile.school;
      },
      hasSchool() {
        return this.getProfile.school.school_id !== '';
      },
      is_course_author() {
        return this.getRoleInCourse === 'author';
      },
      can_add_co_teachers() {
        return (
          this.getRoleInCourse === 'author' || this.getProfile.role === 'admin'
        );
      },
      // TODO: THIS IS A PLACEHOLDER
      co_teachers_in_class() {
        return this.getCourseDetail.users.filter((user) => {
          return user.subscription.role === 'co-teacher';
        });
      },
    },
    methods: {
      setCourseSettings() {
        this.title = this.getCourseDetail.title;
        this.course_is_archived = this.getCourseDetail.is_archived;
        this.section = '';
        this.is_school_course = this.getCourseDetail.school_id !== null;
        if (
          this.getCourseDetail.sections &&
          this.getCourseDetail.sections.length
        ) {
          this.section = this.getCourseDetail.sections[0];
        }
      },
      checkIfSettingsHaveChanged() {
        this.title !== this.getCourseDetail.title ||
        this.section !== this.getCourseDetail.sections[0] ||
        this.is_school_course !== (this.getCourseDetail.school_id !== null)
          ? (this.settings_have_changed = true)
          : (this.settings_have_changed = false);
      },
      inviteCoTeacher() {
        if (!this.getProfile.is_paid) {
          const text =
            'ability to add a co-teacher for all your Parlay courses.';
          EventBus.$emit('open-upgrade-modal', text);
          return;
        }
        const payload = {
          co_teacher_email_to_invite: this.co_teacher_email_to_invite.replace(
            /\s/g,
            ''
          ),
        };
        if (this.co_teacher_email_to_invite === this.getProfile.email) {
          const toast = {
            type: 'Error',
            message: 'You cannot invite yourself!',
            time: 5000,
          };
          EventBus.$emit('createToast', toast);
          this.co_teacher_email_to_invite = '';
        } else {
          const checkCoteachers = this.co_teachers_in_class.filter(
            (c) => c.email === this.co_teacher_email_to_invite
          );
          if (checkCoteachers.length > 0) {
            const toast = {
              type: 'Warning',
              message: 'You have already added this co-teacher to the list!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            this.co_teacher_email_to_invite = '';
          } else {
            const payload2 = { email_query: this.co_teacher_email_to_invite };
            adminServices
              .getCoteacherByEmail(payload2)
              .then(({ data }) => {
                if (data[0].role === 'student') {
                  const toast = {
                    type: 'Error',
                    message:
                      'The email inserted belongs to a student account! You cannot add this user as co-teacher!',
                    time: 5000,
                  };
                  EventBus.$emit('createToast', toast);
                } else {
                  inviteCoTeacher(this.getCourseDetail.id, payload).then(() => {
                    const toast = {
                      type: 'Success',
                      message: 'The co-teacher has been successfully invited!',
                      time: 5000,
                    };
                    EventBus.$emit('createToast', toast);
                    this.co_teacher_email_to_invite = '';
                  });
                }
                this.co_teacher_email_to_invite = '';
              })
              .catch((e) => {
                const toast = {
                  type: 'Error',
                  message:
                    'There is no Parlay Account associated with this email address',
                  time: 5000,
                };
                EventBus.$emit('createToast', toast);
                this.co_teacher_email_to_invite = '';
                console.error(e);
              });
          }
        }
      },
      selectCoTeacherToRemove(co_teacher) {
        this.co_teacher_selected_to_remove = co_teacher;
        this.show_confirm_co_teacher_removal = true;
      },
      removeCoTeacher() {
        removeCoTeacher(
          this.getCourseDetail.id,
          this.co_teacher_selected_to_remove.email
        ).then(() => {
          this.$store.dispatch(
            'deleteUserFromCourse',
            this.co_teacher_selected_to_remove.id
          );
          if (
            this.co_teacher_selected_to_remove.email ===
            this.co_teacher_email_to_invite
          )
            this.email_error = '';
        });
      },
      deleteCourse() {
        this.$store
          .dispatch('deleteCourse', this.$route.params.courseID)
          .then(() => {
            const toast = {
              type: 'Success',
              message: 'Course deleted with success!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
            this.$router.push({ name: 'dashboard' });
          })
          .catch((e) => {
            console.error(e);
            const toast = {
              type: 'Error',
              message: 'There was an error deleting the Course!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
      },
      archiveCourse() {
        this.$store
          .dispatch('archiveCourse', this.$route.params.courseID)
          .then(() => {
            this.course_is_archived = !this.course_is_archived;
            const message = `Course ${
              this.course_is_archived ? 'archived' : 'restored'
            } with sucess!`;
            const toast = { type: 'Success', message, time: 5000 };
            EventBus.$emit('createToast', toast);
          })
          .catch((e) => {
            console.error(e);
            const toast = {
              type: 'Error',
              message: 'There was an error archiving the Course!',
              time: 5000,
            };
            EventBus.$emit('createToast', toast);
          });
      },
      saveSettings() {
        if (this.title || this.section) {
          const school_id = this.is_school_course
            ? this.getProfile.school.school_id
            : null;
          const payload = {
            title: this.title,
            school_id,
            sections: [this.section],
          };
          updateCourse(this.$route.params.courseID, payload)
            .then((res) => {
              res.data.source = 'settings';
              store.dispatch('setCourseDetail', res.data);
              this.settings_have_changed = false;
              const toast = {
                type: 'Success',
                message: 'Course settings saved with success!',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            })
            .catch((e) => {
              console.error(e);
              const toast = {
                type: 'Error',
                message: 'There was an error in updating Course settings!',
                time: 5000,
              };
              EventBus.$emit('createToast', toast);
            });
        }
      },
      backToPrevious() {
        let route = {
          name: 'course.roundtables.default',
          params: { courseID: this.$route.params.courseID },
        };
        if (this.course_is_archived || this.$route.query.dashboard)
          route = { name: 'dashboard' };
        return this.$router.push(route);
      },
    },
    mounted() {
      if (
        !this.getCourseDetail.id ||
        this.getCourseDetail.id !== this.$route.params.courseID ||
        this.$store.state.isToFecth === true
      ) {
        this.$store
          .dispatch('fetchCourseDetails', this.$route.params.courseID)
          .then(() => {
            this.setCourseSettings();
            this.$store.commit('setFecthComponent', false);
          });
      } else this.setCourseSettings();
    },
    beforeDestroy() {
      this.leaveCourseSocket(this.$route.params.courseID);
    },
  });
</script>
<style scoped>
  .row {
    margin: 0;
  }
  .toggle-checkbox:checked {
    @apply right-0 border-primary;
    right: 0;
    border-color: #72ccd7;
  }
  .toggle-checkbox:checked + .toggle-label {
    @apply bg-primary;
    background-color: #72ccd7;
  }
</style>

<style scoped lang="stylus">
  .co-teachers
    min-height: calc(100vh - 338px)
    overflow: auto
</style>



// WEBPACK FOOTER //
// generalSettings.vue?d29c2884